import {makeAutoObservable, observable, runInAction} from 'mobx'
import {Lang} from 'yougile-lang'
import {logger, Time, UUID} from 'yougile-util'
import {
  ChatMessage,
  DataObjectId,
  extensionPublicHubTitlePrefix,
  GroupChat,
  Hub,
  newsCompanyId,
  SystemMessageUtils
} from 'yougile-model'
import {Chat} from '@api/model/chat'
import {Accounts} from '@client/accounts'
import {EventBus} from '@client/event-bus'
import {
  backFromChatEvent,
  chatEditStateChangedEvent,
  chatForwardStateChangedEvent,
  chatReplyStateChangedEvent
} from '@client/events'
import {PermissionsChecker} from '@client/permissions-checker'
import {Debouncer} from '@common/debouncer'
import {Tracking, TrackingEvent} from '@common/tracking'
import {markUnreadUpdater} from '@components/common/base/chat-message/mark-important-button'
import {createUseStore} from '@containers/hooks'
import {getLastSeenMessageTime, readChatMessages} from '@extensions/chat'
import {ChatSettings} from '@extensions/chat-settings'
import {ChatUtils} from '@extensions/chat-utils'
import {Feedback} from '@extensions/feedback'
import {globals} from '@extensions/globals-base'
import {FileViewData} from '@extensions/message-utils'
import {News} from '@extensions/news'
import {NotificationSettingsService} from '@extensions/notifications-settings-service'
import {Task} from '@extensions/task'
import {Sync} from '../../../client/sync'
import {showDeleteConfirm} from '../../common/base/notifications'
import {rightPanelStore} from '../right-panel'

export type ChatMessagesScroll = 'bottom' | 'show-unseen-icon'

const messagesPortionToLoad = 50
const newSinceDebounce = new Debouncer(50)

interface State {
  messageToForward?: ChatMessage
  messageToReply?: ChatMessage
  messageToEdit?: ChatMessage
  firstUnread: number
  lastReadOnInit: number
  newSince: number
  lastSeenScrolled: number
  messagesList: ChatMessage[]
  hub?: Hub
  maxMessages: number
  canSendMessages: boolean
  noTabs: boolean
  groupChat: boolean
  whoIsTyping: DataObjectId[]
  chatMessagesScroll?: ChatMessagesScroll
  selectedFileToViewIndex: number
  selectedFilesToView: FileViewData[]
  messagesContainerElement?: HTMLDivElement
}

export class ChatStore {
  state: State = {
    firstUnread: -1,
    lastReadOnInit: -1,
    newSince: Number.MAX_SAFE_INTEGER,
    lastSeenScrolled: Number.MAX_SAFE_INTEGER,
    messagesList: [],
    maxMessages: messagesPortionToLoad,
    canSendMessages: true,
    noTabs: true,
    groupChat: false,
    whoIsTyping: [],
    selectedFileToViewIndex: -1,
    selectedFilesToView: [],
    messagesContainerElement: undefined
  }

  private latestPostTime = 0
  private msgLen = 0
  private timeoutId: ReturnType<typeof setTimeout> | undefined
  private upTypingUid: UUID | undefined

  constructor(hub: Hub | undefined, noTabs: boolean) {
    makeAutoObservable(this, {state: observable.shallow}, {autoBind: true})
    this.state.hub = hub
    this.state.noTabs = noTabs
  }

  setMessagesContainerElement(ref: HTMLDivElement): void {
    this.state.messagesContainerElement = ref
  }

  get messagesContainerElement(): HTMLDivElement | undefined {
    return this.state.messagesContainerElement
  }

  setMessageToReply(msg: ChatMessage): void {
    this.state.messageToReply = msg
    this.state.messageToEdit = undefined
    this.state.messageToForward = undefined
    EventBus.dispatch(chatReplyStateChangedEvent(msg))
  }

  setMessageToForward(msg?: ChatMessage): void {
    this.state.messageToForward = msg
    this.state.messageToReply = undefined
    this.state.messageToEdit = undefined
    EventBus.dispatch(chatForwardStateChangedEvent(msg))
  }

  setSelectedFilesToView(
    selectedFilesToView: FileViewData[],
    fileIndex: number
  ): void {
    this.state.selectedFilesToView = selectedFilesToView
    this.state.selectedFileToViewIndex = fileIndex
  }

  closeFileViewer(): void {
    this.state.selectedFilesToView = []
  }

  cancelReply(): void {
    this.state.messageToReply = undefined
    EventBus.dispatch(chatReplyStateChangedEvent(undefined))
  }

  startEdit(msg: ChatMessage): void {
    this.state.messageToEdit = msg
    this.state.messageToReply = undefined
    this.state.messageToForward = undefined
    EventBus.dispatch(chatEditStateChangedEvent(msg))
  }

  async deleteMessage(msg: ChatMessage): Promise<void> {
    if (
      msg &&
      this.isExtensionCommentsEditor &&
      (await showDeleteConfirm(Lang.msg.messageDeleteConfirm))
    ) {
      msg.deleted = true
      this.state.hub?.commit()
    }
  }

  stopEdit(): void {
    this.state.messageToEdit = undefined
    EventBus.dispatch(chatEditStateChangedEvent(undefined))
  }

  setFirstUnread(firstUnread: number): void {
    this.state.firstUnread = firstUnread
  }

  markUnread(timestamp: number): void {
    const hub = this.state.hub
    if (hub?.chat) {
      void Tracking.send(TrackingEvent.chatPanelBack)
      EventBus.dispatch(backFromChatEvent())

      // Timeout is required for this to happen after scroll observer and right panel animation ran
      setTimeout(() => {
        hub.chat.setLastSeen(globals.currentUserId, timestamp - 1)
        hub.commit()

        void Tracking.send(TrackingEvent.chatMarkUnread)
      }, 350)
    }
  }

  markImportant(timestamp: number): void {
    const hub = this.state.hub
    if (hub?.chat) {
      void Tracking.send(TrackingEvent.chatPanelBack)
      EventBus.dispatch(backFromChatEvent())

      // Timeout is required for this to happen after scroll observer and right panel animation ran
      setTimeout(() => {
        hub.data.markImportantMap ??= {}
        hub.data.markImportantMap[globals.currentUserId] = timestamp
        hub.commit()

        markUnreadUpdater.update()

        void Tracking.send(TrackingEvent.chatMarkImportant)
      }, 350)
    }
  }
  cleanImportant(): void {
    const hub = this.state.hub
    if (hub?.chat) {
      if (hub.data?.markImportantMap?.[globals.currentUserId] === undefined) {
        return
      }

      // Timeout is required for this to happen after scroll observer and right panel animation ran
      setTimeout(() => {
        hub.data.markImportantMap ??= {}
        hub.data.markImportantMap[globals.currentUserId] = undefined
        hub.commit()
        markUnreadUpdater.update()
      }, 350)
    }
  }

  showMore(): void {
    const first = this.state.messagesList[0]
    const id = `chat-msg-${first.timestamp}`

    this.state.maxMessages += messagesPortionToLoad
    this.updateMessagesList()

    setTimeout(() =>
      ChatUtils.goToId(this.messagesContainerElement, id, {
        disallowExpand: true,
        recur: 0,
        shouldHighlight: false
      })
    )
  }
  showImportantMessage(timestamp?: number): void {
    if (!timestamp) {
      return
    }

    const idx = this.state.messagesList.findIndex(
      message => message.timestamp === timestamp
    )

    if (idx === -1) {
      logger.error('Cannot find message by timestamp', timestamp)
      return
    }

    const countByDown = this.state.messagesList.length - idx + 1

    while (countByDown > this.state.maxMessages - 1) {
      this.state.maxMessages += messagesPortionToLoad
    }
  }

  get isShowMoreVisible(): boolean {
    return this.state.messagesList.length > this.state.maxMessages - 1
  }

  private applyAdditionalFilters(messages: ChatMessage[]): ChatMessage[] {
    let filteredMessages = messages

    try {
      const notificationSettings =
        NotificationSettingsService.getNotificationSettings()
      const messageFilter = NotificationSettingsService.getTaskMessageFilter(
        notificationSettings,
        this.state.hub
      )
      filteredMessages = filteredMessages.filter(messageFilter)
    } catch (error) {
      logger.warn('Failed to apply notification settings filter', error)
    }

    return filteredMessages
  }

  getAllMessagesList(options?: {includeSystem?: boolean}): ChatMessage[] {
    const hub = this.state.hub
    if (!hub) {
      return []
    }
    const includeSystem = options?.includeSystem ?? true
    const settings = ChatSettings.getSettings()

    const baseFilteredMessages = [...hub.chat.messages.values()]
      .filter(
        m =>
          m?.timestamp &&
          m.text &&
          !m.deleted &&
          (includeSystem
            ? !SystemMessageUtils.isSystemMessage(m) ||
              SystemMessageUtils.isKnownSystemMessage(m)
            : !SystemMessageUtils.isSystemMessage(m))
      )
      .filter(m => {
        if (
          SystemMessageUtils.isSystemMessage(m) &&
          !settings.showReadSystemMessages &&
          (m.timestamp < this.state.lastReadOnInit ||
            this.state.lastReadOnInit < 0)
        ) {
          return false
        }
        return true
      })
      .sort((a, b) => a.timestamp - b.timestamp)

    return this.applyAdditionalFilters(baseFilteredMessages)
  }

  private updateMessagesList(isUpdate = true, scrollBottom = true): void {
    const hub = this.state.hub
    if (!hub) {
      this.state.messagesList = []
      return
    }

    const includeSystem = !this.isNews && !this.isFeedback
    const res = this.getAllMessagesList({includeSystem})
    this.state.messagesList = res.slice(-this.state.maxMessages)
    Chat.activeChatMessagesChange(this.state.hub?.id, this.state.messagesList)
    if (res.length !== this.msgLen) {
      this.msgLen = res.length
      if (isUpdate && scrollBottom) {
        this.scrollBottom()
      }
    }

    const updateNewSince = (): void => {
      runInAction(() => {
        const index = Math.max(this.state.messagesList.length - 1, 0)
        this.state.newSince = this.state.messagesList[index]?.timestamp || 0
      })
    }
    if (isUpdate) {
      newSinceDebounce.runLatest(updateNewSince)
    } else {
      updateNewSince()
    }
  }

  syncOnNew(hub: Hub | undefined, noTabs: boolean): void {
    this.state.hub = hub
    this.state.noTabs = noTabs
    this.state.groupChat = !!hub && GroupChat.isGroupChat(hub)
    this.state.chatMessagesScroll = undefined
    this.state.messageToReply = undefined
    this.state.messageToForward = undefined
    this.state.messageToEdit = undefined

    this.dispatch()

    if (!hub) {
      return
    }

    this.state.lastReadOnInit =
      hub.chat.getLastSeenMessage(globals.currentUserId) + 1

    this.upTyping()
    this.upTypingUid = Accounts.addListener({
      account: 'current',
      cb: this.upTyping
    })

    const userId = globals.currentUserId
    this.state.maxMessages = messagesPortionToLoad

    hub.data?.markImportantMap?.[userId]

    const markedImportant = this.state.hub?.data?.markImportantMap?.[userId]
    this.showImportantMessage(markedImportant)

    this.msgLen = 0

    this.state.lastSeenScrolled = getLastSeenMessageTime(hub)
    this.updateMessagesList(false)
    this.updateFirstUnread()
    this.scrollToFirstUnread()

    this.state.canSendMessages =
      noTabs || PermissionsChecker.checkSendMessages(hub)
    if (hub.companyId === newsCompanyId) {
      News.markSeen(hub)
    }

    ChatUtils.requestMore = (cb: (avail: number, total: number) => void) => {
      const total = hub.chat.getMessagesCount()
      if (total > this.state.maxMessages) {
        this.state.maxMessages += messagesPortionToLoad
        this.updateMessagesList()
        setTimeout(() => cb(this.state.maxMessages, total))
      } else {
        cb(this.state.maxMessages, total)
      }
    }
  }

  syncOnUpdate(init = false): void {
    const hub = this.state.hub
    if (!hub) {
      return
    }

    const autoRead =
      init ||
      (this.state.chatMessagesScroll === 'bottom' &&
        // NOTE: syncOnUpdate is called when the chat UI is rendered,
        // but the right-panel might be collapsed (not visible for user).
        //
        // So, we allow auto-reading messages only if the right
        // chat is visible for user. Exclude extension comments,
        // as they aren't in the right panel.
        (rightPanelStore.state.chatPanel.opened || this.isExtensionComments))

    this.updateMessagesList(true, autoRead)
    if (autoRead) {
      this.autoread()
    }

    this.state.canSendMessages =
      this.state.noTabs || PermissionsChecker.checkSendMessages(hub)
    if (hub.companyId === newsCompanyId) {
      News.markSeen(hub)
    }
  }

  public autoread(): void {
    if (!this.state.hub) {
      return
    }

    const canAutoRead = !globals.silentMode && !globals.readonly
    if (!canAutoRead) {
      return
    }

    if ((location.hash || '').match(/chat:|direct:|-/i)) {
      const seen = getLastSeenMessageTime(this.state.hub)
      const msg = this.state.hub.chat.lastMessage()
      if (msg && (!seen || msg.timestamp > seen)) {
        void readChatMessages(this.state.hub)
      }
    }
  }

  private dispatch(): void {
    this.upTypingUid && Accounts.removeListener(this.upTypingUid)
    this.timeoutId && clearTimeout(this.timeoutId)
  }

  scrollBottom(afterPostMessage = false): void {
    const el = this.messagesContainerElement
    if (!el || !this.state.hub) {
      return
    }

    const now = Time.now()
    if (afterPostMessage) {
      this.setLastSeenScrolled(now)
      this.latestPostTime = now
    }

    setTimeout(() => {
      if (this.isNews && now - this.latestPostTime > 10_000) {
        ChatUtils.scrollTop(el)
        return
      }
      if (globals.openedChatNoScroll) {
        globals.openedChatNoScroll = false
        return
      }
      ChatUtils.scrollBottom(el)
    }, 10)
  }

  updateFirstUnread(): void {
    this.cleanImportant()

    if (!this.state.hub || this.state.hub?.companyId === newsCompanyId) {
      this.setFirstUnread(-1)
      return
    }
    const lastSeenTimestamp = getLastSeenMessageTime(this.state.hub)
    const msg = this.state.messagesList.find(m => {
      let flag = m.timestamp > lastSeenTimestamp
      // Consider system messages that were results of current user's actions as seen
      if (SystemMessageUtils.isKnownSystemMessage(m)) {
        const actionBy = m?.properties?.actionBy
        flag &&= actionBy !== globals.currentUserId
      }
      return flag
    })
    this.setFirstUnread(msg ? msg.timestamp : -1)
  }

  scrollToTimestamp(timestamp: number): void {
    // when timestamp is -1,here it's a case to scroll to bottom
    const parent = this.messagesContainerElement
    if (!parent) {
      return
    }

    const unreadElId = `chat-unread-${timestamp}`
    const messageElId = `chat-msg-${timestamp}`
    if (document.getElementById(unreadElId)) {
      ChatUtils.goToId(parent, unreadElId)
    } else if (document.getElementById(messageElId)) {
      ChatUtils.goToId(parent, messageElId)
    } else {
      parent.scrollTop = parent.scrollHeight
    }
  }

  scrollToFirstUnread(): void {
    const userId = globals.currentUserId
    const markedImportant = this.state.hub?.data?.markImportantMap?.[userId]

    // should run after all elements render
    setTimeout(() => {
      if (markedImportant) {
        this.scrollToTimestamp(markedImportant)
      } else {
        this.scrollToTimestamp(this.state.firstUnread)
      }
    }, 50)
  }

  upTyping(): void {
    if (!this.state.hub) {
      return
    }
    this.state.whoIsTyping = Accounts.getAllTypingAt(this.state.hub.id).filter(
      id => id !== globals.currentUserId
    )
    if (this.state.whoIsTyping.length) {
      this.timeoutId = setTimeout(this.upTyping, 3000)
    }
  }

  get isNews(): boolean {
    return (
      this.state.hub?.companyId === newsCompanyId && !this.isExtensionComments
    )
  }

  get isComments(): boolean {
    return this.isNews || this.isExtensionComments
  }

  get isExtensionCommentsEditor(): boolean {
    return Sync.isNewsEditor && this.isExtensionComments
  }

  get isTask(): boolean {
    return !!this.state.hub && Task.isTask(this.state.hub)
  }

  get canForwardMessage(): boolean {
    // NOTE: Currently, we do not allow forwarding messages from extension comments,
    // because the forward message popup is implemented for the right-panel chat,
    // has a fixed width, and looks awkward when displayed over the extension gallery.
    //
    // Once it's reimplemented, feel free to enable message forwarding from extension comments.
    // You'll also need to implement "click to forwarded message" functionality
    // to correctly open the extensions gallery with the related comment.
    return !this.isExtensionComments
  }

  get isExtensionComments(): boolean {
    return (
      this.state.hub?.companyId === newsCompanyId &&
      this.state.hub?.title.startsWith(extensionPublicHubTitlePrefix)
    )
  }

  get isFeedback(): boolean {
    return Feedback.column && this.state.hub?.location === Feedback.column.id
  }

  get showNoMessages(): boolean {
    return (
      !this.isNews &&
      !this.state.messagesList.length &&
      !!this.state.hub &&
      this.state.canSendMessages
    )
  }

  setLastSeenScrolled(lastSeen: number): void {
    const lastSeenScrolled = this.state.lastSeenScrolled
    this.state.lastSeenScrolled = Math.max(lastSeenScrolled, lastSeen)
    const userId = globals.currentUserId
    const res = this.state.hub?.chat.read(userId, this.state.lastSeenScrolled)
    if (res) {
      this.state.hub?.commit()
    }
  }

  setChatMessagesScroll(value: ChatMessagesScroll | undefined): void {
    this.state.chatMessagesScroll = value
  }
}

export const [ChatContext, useChatStore] = createUseStore(ChatStore)
